import {Component, ContentChildren, EventEmitter, Input, NgZone, Output, QueryList, ViewChild} from '@angular/core';
import {IonicSlides} from '@ionic/angular';
import SwiperCore from 'swiper';
import Swiper, {Pagination} from 'swiper';
import {SwiperComponent} from 'swiper/angular';
import {IziviaSlideDirective} from '../../../directives/iziviaSlide/iziviaSlide.directive';

// IonicSlides must be the last module.
// This will let it automatically customize the settings of several modules for smooth experience
SwiperCore.use([Pagination, IonicSlides]);

/**
 * This is a wrapper component around Swiper.js following deprecation of ion-slides.
 *
 * Due to how Swiper.js integrate with Angular, usage must look like the following:
 * ```
 * <izivia-slides>
 *     <izivia-slide>
 *         <ng-template iziviaSlideTemplate>
 *             <!-- Slide 1 -->
 *         </ng-template>
 *     </izivia-slide>
 *
 *     <izivia-slide>
 *         <ng-template iziviaSlideTemplate>
 *             <!-- Slide 2 -->
 *         </ng-template>
 *     </izivia-slide>
 * </izivia-slides>
 * ```
 *
 * or by using `*ngFor`:
 *
 * ```
 * <izivia-slides>
 *     <izivia-slide *ngFor="let media of medias">
 *         <ng-template iziviaSlideTemplate>
 *             <!-- Slide content: {{media}} -->
 *         </ng-template>
 *     </izivia-slide>
 * </izivia-slides>
 * ```
 *
 * This component enables only a limited subset of features from Swiper.js and provide isolation of the third party lib.
 *
 *
 * @see IziviaSlideDirective
 * @see IziviaSlideTemplateDirective
 *
 * Useful documentation links:
 * <ul>
 *     <li>Properties: https://swiperjs.com/swiper-api#parameters</li>
 *     <li>Events: https://swiperjs.com/swiper-api#parameters</li>
 * </ul>
 *
 * To register additional modules, you need to :
 * <ul>
 *     <li>Import the module style in the _slides.scss file of your app</li>
 *     <li>Add the SwiperModule to the modules array property of SwiperCore.use (top of the file)</li>
 * </ul>
 *
 * To add new events, remember to wrap broadcasting with NgZone because all Swiper.js events are emitted outside NgZone for better performance.
 */
@Component({
    selector: 'izivia-slides',
    templateUrl: './iziviaSlides.component.html',
    styleUrls: ['./iziviaSlides.component.scss']
})
export class IziviaSlidesComponent {
    /**
     * Workaround to keep encapsulating Swiper.js as using <ng-template swiperSlide><ng-content></ng-content></ng-template> is not possible (Angular design)
     * https://github.com/nolimits4web/swiper/discussions/4587
     *
     */
    @ContentChildren(IziviaSlideDirective, {descendants: true}) contents: QueryList<IziviaSlideDirective>;

    @ViewChild('swiper') swiper: SwiperComponent;

    /**
     * Index number of initial slide
     */
    @Input() initialSlideIndex: number = 0;
    /**
     * Number of slides per view (slides visible at the same time on slider's container)
     */
    @Input() slidesPerView: number = 1;
    /**
     * When true, only images within visible slides will be loaded
     */
    @Input() lazyLoadImages: boolean = false;
    /**
     * When true, show pagination bullets
     */
    @Input() pagination: boolean = true;
    /**
     * Duration of transition between slides (in ms)
     */
    @Input() transitionDurationInMs: number = 300;
    /**
     * When true, it will hide all navigation elements and won't respond to any events and interactions
     */
    @Input() disabled: boolean = false;

    /**
     * Emitted after slider initialization
     */
    @Output() didLoad: EventEmitter<void> = new EventEmitter();
    /**
     * Emitted when a transition toward the first slide has ended (active index = 0).
     */
    @Output() reachStart: EventEmitter<void> = new EventEmitter();
    /**
     * Emitted when a transition toward the last slide has ended.
     */
    @Output() reachEnd: EventEmitter<void> = new EventEmitter();

    /**
     * Emitted before the transition to another slide started.
     */
    @Output() slideWillChange: EventEmitter<void> = new EventEmitter();

    /**
     * Emitted after the active slide has changed. The new active index is returned.
     */
    @Output() slideDidChange: EventEmitter<number> = new EventEmitter();


    constructor(private readonly zone: NgZone) {
    }

    broadcastDidLoad(): void {
        this.zone.run(
            () => this.didLoad.emit()
        );
    }

    broadcastWillChange(): void {
        this.zone.run(
            () => this.slideWillChange.emit()
        );
    }

    broadcastActiveIndex(event: [Swiper]): void {
        const [swiper] = event;
        this.zone.run(
            () => {
                this.slideDidChange.emit(swiper.activeIndex);

                if (swiper.isBeginning) {
                    this.reachStart.emit();
                }

                if (swiper.isEnd) {
                    this.reachEnd.emit();
                }
            }
        );
    }

    broadcastReachStart(): void {
        this.zone.run(
            () => this.reachStart.emit()
        );
    }

    broadcastReachEnd(): void {
        this.zone.run(
            () => this.reachEnd.emit()
        );
    }

    /**
     * Change active slide to the next one if end has not been reached
     */
    next(): void {
        this.swiper.swiperRef.slideNext(this.transitionDurationInMs);
    }

    /**
     * Change active slide to the previous one if active index is not the 0
     */
    previous(): void {
        this.swiper.swiperRef.slidePrev(this.transitionDurationInMs);
    }
}
